import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type {
  OverlayLayer,
  ReelScene,
  TextOverlayLayer,
  WatermarkConfig,
} from "../../api";

/** Returns the patch required to scale an overlay by `factor` while keeping
 *  the user-visible "scale" knob untouched — we bake the change into the
 *  size field that's idiomatic for the kind so future inspector edits match
 *  what the user just dragged. */
function patchForScale(overlay: OverlayLayer, factor: number): Partial<OverlayLayer> {
  switch (overlay.kind) {
    case "text":
      return { fontSize: clamp(Math.round(overlay.fontSize * factor), 12, 400) };
    case "badge":
      return { fontSize: clamp(Math.round(overlay.fontSize * factor), 8, 200) };
    case "icon":
      return { sizePct: clampNum(overlay.sizePct * factor, 1, 80) };
    case "shape":
    case "image":
      return {
        widthPct: clampNum(overlay.widthPct * factor, 0.5, 120),
        heightPct: clampNum(overlay.heightPct * factor, 0.5, 120),
      };
    case "group":
      // Groups have no visual presence — scaling is a no-op.
      return {};
  }
}

function clampNum(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** Friendly tag label + glyph per overlay kind (used in the selection chip). */
function overlayBadgeForKind(o: OverlayLayer): { glyph: string; label: string } {
  switch (o.kind) {
    case "text":
      return { glyph: "T", label: "Text" };
    case "shape":
      return { glyph: "▭", label: "Shape" };
    case "icon":
      return { glyph: "✦", label: "Icon" };
    case "badge":
      return { glyph: "●", label: "Badge" };
    case "image":
      return { glyph: "🖼", label: "Bild" };
    case "group":
      return { glyph: "📁", label: "Gruppe" };
  }
}

/**
 * Direct-manipulation overlay that sits absolutely on top of the player.
 * Lets the user drag the logo (watermark) and the selected scene's content
 * around — feels exactly like grabbing things "in the image".
 *
 * The overlay is sized to the visible (rendered) player rectangle. All
 * coordinate math converts pixels → percentage of the *composition*, so
 * the values we emit are resolution-independent.
 */
export type SceneOffsetPatch = {
  offsetX?: number; // px in composition space
  offsetY?: number; // px in composition space
  scale?: number;   // 0.4 .. 1.6
};

export type PlayerEditOverlayProps = {
  enabled: boolean;
  /** Visible (rendered) size of the player container. */
  displayWidth: number;
  displayHeight: number;
  /** Composition (logical) size — used for px↔% mapping. */
  compositionWidth: number;
  compositionHeight: number;
  /** Watermark config — drives the live position of the logo handle. */
  watermark?: WatermarkConfig;
  /**
   * If set, the edit overlay only finds / mutates / measures the watermark
   * DOM node belonging to this specific scene (matched via
   * `data-scene-uid`). This is essential now that each scene renders its
   * own logo with its own settings: without this filter a drag would
   * touch every clip's logo simultaneously.
   */
  wmSceneUid?: string;
  onWatermarkChange?: (next: WatermarkConfig) => void;
  /** Currently selected scene (for "scene content" drag). */
  selectedScene?: ReelScene | null;
  onScenePatch?: (uid: string, patch: SceneOffsetPatch) => void;
  /** All overlays in the reel — used to render selection boxes for the visible ones. */
  overlays?: OverlayLayer[];
  /** Currently selected overlay of any kind (drives drag/resize/edit handles). */
  selectedOverlay?: OverlayLayer | null;
  /** Current playhead frame — overlays only show selectable boxes when visible at this frame. */
  playheadFrame?: number;
  /** FPS — needed to convert overlay's fromSec/durSec into a visibility test. */
  fps?: number;
  /** Single-source selection callback. Pass "" for explicit deselect. */
  onSelectUid?: (uid: string) => void;
  /** Patch an overlay's properties. The patch must be valid for the overlay's
   *  `kind` — the caller (Composer) routes it through a kind-aware updater. */
  onOverlayPatch?: (uid: string, patch: Partial<OverlayLayer>) => void;
  /** Quick-add a text overlay at the playhead. */
  onAddTextOverlay?: () => void;
  /** Receive an element-library drop. xPct/yPct are the drop position in
   *  composition-space (0..100). The composer materializes the template,
   *  injects xPct/yPct, and inserts it at the current playhead. */
  onDropElement?: (elementId: string, xPct: number, yPct: number) => void;
  /** When true, all drag handles are non-interactive (preview-locked). */
  locked?: boolean;
  /** Snap dragged elements to a grid + show grid overlay during drag. */
  snap?: boolean;
  /** Number of grid columns when snap is enabled. Defaults to 8. */
  gridCols?: number;
  /** Number of grid rows when snap is enabled. Defaults to 12. */
  gridRows?: number;
  /**
   * Called when a drag begins. The Composer uses this to PAUSE the player
   * for the duration of the drag — otherwise the player is continuously
   * decoding frames AND re-rendering on every pointer-move (60Hz inputProps
   * churn), which causes the visible stutter and pixel artifacts.
   */
  onDragStart?: () => void;
  /** Called when the drag ends — Composer resumes playback if it was playing before. */
  onDragEnd?: () => void;
};

type DragState =
  | { kind: "logo"; startX: number; startY: number; baseXPct: number; baseYPct: number }
  | { kind: "logo-resize"; startX: number; startY: number; baseSize: number }
  | {
      kind: "scene";
      startX: number;
      startY: number;
      baseOffX: number;
      baseOffY: number;
    }
  | {
      kind: "scale";
      startX: number;
      startY: number;
      baseScale: number;
    }
  | {
      kind: "overlay-move";
      uid: string;
      startX: number;
      startY: number;
      baseXPct: number;
      baseYPct: number;
    }
  | {
      kind: "overlay-scale";
      uid: string;
      startX: number;
      startY: number;
      baseScale: number;
      baseFontSize: number;
    }
  | {
      kind: "overlay-rotate";
      uid: string;
      startAngleDeg: number;
      baseRotate: number;
      centerX: number;
      centerY: number;
    };

/** Compute pixel coordinates of the watermark handle in *display* space.
 *  Wrappt das nackte Logo (kein Pill mehr). Wir nehmen `wm.size` (Logo-Höhe
 *  in Komp-Pixeln) und skalieren auf Display-Pixel; Breite ≈ Höhe (Logo ist
 *  weitestgehend quadratisch — der tatsächliche Drag/Resize verwendet die
 *  live gemessene Bounding-Box aus dem iframe). */
function watermarkHandlePos(
  wm: WatermarkConfig,
  compW: number,
  compH: number,
  displayW: number,
  displayH: number,
): { left: number; top: number; w: number; h: number } {
  const rX = displayW / Math.max(1, compW);
  const rY = displayH / Math.max(1, compH);
  const handleH = Math.max(20, wm.size * rY);
  const handleW = handleH; // Logo ist ~quadratisch.

  if (typeof wm.xPct === "number" && typeof wm.yPct === "number") {
    const cx = (wm.xPct / 100) * displayW;
    const cy = (wm.yPct / 100) * displayH;
    return { left: cx - handleW / 2, top: cy - handleH / 2, w: handleW, h: handleH };
  }

  const padX = Math.max(0, wm.paddingPx * rX);
  const padY = Math.max(0, wm.paddingPx * rY);
  const offX = ((wm.offsetX ?? 0) / 100) * displayW;
  const offY = ((wm.offsetY ?? 0) / 100) * displayH;

  const horizSign = wm.position.includes("l") ? 1 : -1;
  const vertSign = wm.position.includes("t") ? 1 : -1;

  const left = wm.position.includes("l")
    ? padX + horizSign * offX
    : displayW - handleW - (padX + horizSign * offX);
  const top = wm.position.includes("t")
    ? padY + vertSign * offY
    : displayH - handleH - (padY + vertSign * offY);

  return { left, top, w: handleW, h: handleH };
}

/**
 * Snap a value to the closest multiple of `step` if within `threshold`.
 * Returns the unchanged value when no snap should occur.
 */
function snapToStep(value: number, step: number, threshold: number): { value: number; snapped: boolean } {
  if (step <= 0) return { value, snapped: false };
  const nearest = Math.round(value / step) * step;
  if (Math.abs(nearest - value) <= threshold) {
    return { value: nearest, snapped: true };
  }
  return { value, snapped: false };
}

export const PlayerEditOverlay: React.FC<PlayerEditOverlayProps> = ({
  enabled,
  displayWidth,
  displayHeight,
  compositionWidth,
  compositionHeight,
  watermark,
  wmSceneUid,
  onWatermarkChange,
  selectedScene,
  onScenePatch,
  overlays = [],
  selectedOverlay,
  playheadFrame = 0,
  fps = 30,
  onSelectUid,
  onOverlayPatch,
  onAddTextOverlay,
  onDropElement,
  locked = false,
  snap = false,
  gridCols = 8,
  gridRows = 12,
  onDragStart,
  onDragEnd,
}) => {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [snapHits, setSnapHits] = useState<{ x: number | null; y: number | null }>({
    x: null,
    y: null,
  });

  // Während eines Overlay-Drags akkumulierte Property-Änderungen.
  // WICHTIG: Wir patchen den React-State NICHT pro Maus-Move, weil das die
  // gesamte Composition neu baut und das iframe-srcDoc reloadet (Stottern +
  // Playhead-Reset). Stattdessen mutieren wir den DOM-Knoten im iframe
  // direkt für die visuelle Vorschau und committen erst auf pointerup.
  const liveDragPatchRef = useRef<{ uid: string; patch: Partial<OverlayLayer> } | null>(null);
  const liveDragNodeRef = useRef<HTMLElement | null>(null);

  /** Holt den Iframe-DOM-Knoten zu einer Overlay-UID (Composition läuft im
   *  <iframe srcDoc>, daher müssen wir querySelector am contentDocument
   *  laufen lassen, nicht am Parent). */
  const findOverlayDomNode = useCallback((uid: string): HTMLElement | null => {
    const root = overlayRef.current?.parentElement;
    if (!root) return null;
    const inParent = root.querySelector(`[data-overlay-uid="${uid}"]`) as HTMLElement | null;
    if (inParent) return inParent;
    const iframe = root.querySelector("iframe") as HTMLIFrameElement | null;
    try {
      return (iframe?.contentDocument?.querySelector(`[data-overlay-uid="${uid}"]`) as HTMLElement | null) ?? null;
    } catch {
      return null;
    }
  }, []);

  /**
   * Holt die Watermark-DOM-Knoten im iframe, die zu unserer Edit-Scope gehören.
   *
   * Seit Watermark-Settings PRO SZENE konfigurierbar sind, rendert jede Scene
   * ihr eigenes Logo. Wir mutieren beim Drag/Resize NUR den Knoten der gerade
   * editierten Scene — sonst würden alle anderen Clip-Logos sofort dieselbe
   * Position annehmen und der User-Eindruck „pro Scene unabhängig" wäre kaputt.
   *
   * Filter:
   *  - `wmSceneUid` gesetzt → nur `[data-scene-uid="<uid>"][data-clip-wm]`
   *  - sonst (kein Scope) → leeres Array, weil ein „globales" Logo nicht
   *    mehr existiert.
   */
  const findWatermarkDomNodes = useCallback((): HTMLElement[] => {
    const root = overlayRef.current?.parentElement;
    if (!root) return [];
    const iframe = root.querySelector("iframe") as HTMLIFrameElement | null;
    try {
      const sel = wmSceneUid
        ? `[data-clip-wm][data-scene-uid="${wmSceneUid}"]`
        : "";
      if (!sel) return [];
      const list = iframe?.contentDocument?.querySelectorAll(sel);
      return list ? (Array.from(list) as HTMLElement[]) : [];
    } catch {
      return [];
    }
  }, [wmSceneUid]);

  /** Mutiert den DOM-Knoten im iframe live (ohne React-Reload), damit der
   *  Drag visuell verzögerungsfrei wirkt. Spiegelt das Style-Schema aus
   *  `buildOverlayHtml`. */
  const applyLivePatch = useCallback(
    (node: HTMLElement, overlay: OverlayLayer, patch: Partial<OverlayLayer>) => {
      const p = patch as Record<string, unknown>;
      if (typeof p.xPct === "number") node.style.left = `${p.xPct}%`;
      if (typeof p.yPct === "number") node.style.top = `${p.yPct}%`;
      if (typeof p.rotate === "number" || typeof p.scale === "number") {
        const r = typeof p.rotate === "number" ? p.rotate : overlay.rotate;
        const s = typeof p.scale === "number" ? p.scale : overlay.scale;
        node.style.transform = `translate(-50%, -50%) rotate(${r}deg) scale(${s})`;
      }
      switch (overlay.kind) {
        case "image":
        case "shape":
          if (typeof p.widthPct === "number") node.style.width = `${p.widthPct}%`;
          if (typeof p.heightPct === "number") node.style.height = `${p.heightPct}%`;
          break;
        case "icon":
          if (typeof p.sizePct === "number") node.style.width = `${p.sizePct}%`;
          break;
        case "text":
        case "badge": {
          if (typeof p.fontSize === "number") {
            const inner = node.firstElementChild as HTMLElement | null;
            if (inner) inner.style.fontSize = `${p.fontSize}px`;
          }
          break;
        }
        case "group":
          break;
      }
    },
    [],
  );

  // rAF-coalescer: pointer-move events arrive at 120-240 Hz on modern devices,
  // but we only need to update React state at display refresh rate (~60 Hz).
  // Without this throttle, every pointer-move triggers a parent re-render,
  // which re-evaluates inputProps, which makes the Player re-render its entire
  // composition tree → visible stutter + pixel tearing during drags.
  const rafIdRef = useRef<number | null>(null);
  const pendingPatchRef = useRef<(() => void) | null>(null);
  const scheduleUpdate = useCallback((apply: () => void) => {
    pendingPatchRef.current = apply;
    if (rafIdRef.current != null) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      const fn = pendingPatchRef.current;
      pendingPatchRef.current = null;
      if (fn) fn();
    });
  }, []);
  useEffect(() => {
    return () => {
      if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  // Step (display px) per grid cell for the active format.
  const stepX = displayWidth / Math.max(1, gridCols);
  const stepY = displayHeight / Math.max(1, gridRows);
  const snapThreshold = Math.min(stepX, stepY) * 0.25;

  // Composition-space scene offset/scale of selected scene.
  const sceneOffX = useMemo(() => {
    const ex = (selectedScene?.inputs?.extras ?? {}) as Record<string, unknown>;
    return Number(ex.offsetX ?? 0);
  }, [selectedScene]);
  const sceneOffY = useMemo(() => {
    const ex = (selectedScene?.inputs?.extras ?? {}) as Record<string, unknown>;
    return Number(ex.offsetY ?? 0);
  }, [selectedScene]);
  const sceneScale = useMemo(() => {
    const ex = (selectedScene?.inputs?.extras ?? {}) as Record<string, unknown>;
    return Number(ex.scale ?? 1);
  }, [selectedScene]);

  // px ratios composition → display
  const rX = displayWidth / Math.max(1, compositionWidth);
  const rY = displayHeight / Math.max(1, compositionHeight);

  // Logo handle box (display-space). Wir messen — wenn möglich — den live
  // gerenderten Watermark-Knoten im iframe, damit das Handle exakt das Logo
  // umschließt (auch nach Live-Resize ohne State-Commit). Fallback ist die
  // analytische Berechnung aus wm.size + position.
  const [logoLiveBox, setLogoLiveBox] = useState<{ left: number; top: number; w: number; h: number } | null>(null);
  useLayoutEffect(() => {
    const root = overlayRef.current?.parentElement;
    if (!root || !watermark?.enabled) return;
    let raf = 0;
    const tick = (): void => {
      const iframe = root.querySelector("iframe") as HTMLIFrameElement | null;
      try {
        // Nur das Logo der aktuell selektierten Scene messen — Logos
        // anderer Scenes haben jetzt eigene Positionen und dürfen nicht
        // den Drag-Anker stehlen.
        const sel = wmSceneUid
          ? `[data-clip-wm][data-scene-uid="${wmSceneUid}"]`
          : "[data-clip-wm]";
        const node = iframe?.contentDocument?.querySelector(sel) as HTMLElement | null;
        if (node && iframe) {
          const iframeRect = iframe.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();
          const sX = iframeRect.width / Math.max(1, compositionWidth);
          const sY = iframeRect.height / Math.max(1, compositionHeight);
          const r = node.getBoundingClientRect();
          setLogoLiveBox({
            left: iframeRect.left - rootRect.left + r.left * sX,
            top: iframeRect.top - rootRect.top + r.top * sY,
            w: r.width * sX,
            h: r.height * sY,
          });
        } else {
          setLogoLiveBox(null);
        }
      } catch {
        setLogoLiveBox(null);
      }
      raf = window.setTimeout(tick, 100) as unknown as number;
    };
    tick();
    return (): void => { window.clearTimeout(raf); };
  }, [watermark, wmSceneUid, displayWidth, displayHeight, compositionWidth, compositionHeight]);
  const logoBox = useMemo(() => {
    if (!watermark) return null;
    if (logoLiveBox) return logoLiveBox;
    return watermarkHandlePos(watermark, compositionWidth, compositionHeight, displayWidth, displayHeight);
  }, [watermark, logoLiveBox, displayWidth, displayHeight, compositionWidth, compositionHeight]);

  // Scene-content bounding box (display-space) — represents the whole composition,
  // shifted by current offset/scale.
  const sceneBox = useMemo(() => {
    const baseW = displayWidth;
    const baseH = displayHeight;
    const w = baseW * sceneScale;
    const h = baseH * sceneScale;
    const left = (baseW - w) / 2 + sceneOffX * rX;
    const top = (baseH - h) / 2 + sceneOffY * rY;
    return { left, top, w, h };
  }, [displayWidth, displayHeight, sceneScale, sceneOffX, sceneOffY, rX, rY]);

  // ===== Drag handlers =====

  /** Hilfs-Refs für den Logo-Drag: während des Drags werden ALLE
   *  Watermark-Knoten im iframe live mutiert (kein React-State-Patch, sonst
   *  rebuildet die Composition pro Mausbewegung und das iframe-srcDoc
   *  reloadet → Stottern). Commit erfolgt auf pointerup. */
  const logoDragNodesRef = useRef<HTMLElement[]>([]);
  const pendingWmCommitRef = useRef<Partial<WatermarkConfig> | null>(null);

  const onLogoDown = useCallback(
    (e: React.PointerEvent) => {
      if (!enabled || locked || !watermark || !onWatermarkChange) return;
      e.preventDefault();
      e.stopPropagation();
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      onDragStart?.();
      logoDragNodesRef.current = findWatermarkDomNodes();
      pendingWmCommitRef.current = null;
      // Aktuelle Mittelpunkt-Position des Logos in % der Canvas einfangen,
      // egal ob das Logo gerade frei (xPct/yPct) oder corner-anchored
      // platziert ist. Dadurch funktioniert der Drag aus jeder Ausgangslage.
      const handle = logoBox ?? watermarkHandlePos(watermark, compositionWidth, compositionHeight, displayWidth, displayHeight);
      const cxPct = ((handle.left + handle.w / 2) / Math.max(1, displayWidth)) * 100;
      const cyPct = ((handle.top + handle.h / 2) / Math.max(1, displayHeight)) * 100;
      setDrag({
        kind: "logo",
        startX: e.clientX,
        startY: e.clientY,
        baseXPct: cxPct,
        baseYPct: cyPct,
      });
      setHint(snap ? "Logo verschieben · Raster aktiv (Shift = frei)" : "Logo frei verschieben");
    },
    [enabled, locked, watermark, onWatermarkChange, snap, onDragStart, displayWidth, displayHeight, compositionWidth, compositionHeight, logoBox, findWatermarkDomNodes],
  );

  const onLogoResizeDown = useCallback(
    (e: React.PointerEvent) => {
      if (!enabled || locked || !watermark || !onWatermarkChange) return;
      e.preventDefault();
      e.stopPropagation();
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      onDragStart?.();
      logoDragNodesRef.current = findWatermarkDomNodes();
      pendingWmCommitRef.current = null;
      setDrag({
        kind: "logo-resize",
        startX: e.clientX,
        startY: e.clientY,
        baseSize: watermark.size,
      });
      setHint("Logo skalieren — diagonal ziehen");
    },
    [enabled, locked, watermark, onWatermarkChange, onDragStart, findWatermarkDomNodes],
  );

  /** Per-scene lock from the Layers panel — kept separate from the global
   *  `locked` flag so the user can pin a single scene in place while still
   *  editing everything else freely. Stored in `inputs.extras.locked`. */
  const sceneLayerLocked = useMemo(() => {
    const ex = (selectedScene?.inputs?.extras ?? {}) as Record<string, unknown>;
    return Boolean(ex.locked);
  }, [selectedScene]);

  const onSceneDown = useCallback(
    (e: React.PointerEvent) => {
      if (!enabled || locked || sceneLayerLocked || !selectedScene || !onScenePatch) return;
      e.preventDefault();
      e.stopPropagation();
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      onDragStart?.();
      setDrag({
        kind: "scene",
        startX: e.clientX,
        startY: e.clientY,
        baseOffX: sceneOffX,
        baseOffY: sceneOffY,
      });
      setHint(
        snap
          ? `Scene „${selectedScene.sceneId}" · Raster aktiv (Shift = frei)`
          : `Scene „${selectedScene.sceneId}" verschieben`,
      );
    },
    [enabled, locked, sceneLayerLocked, selectedScene, onScenePatch, sceneOffX, sceneOffY, snap, onDragStart],
  );

  const onScaleDown = useCallback(
    (e: React.PointerEvent) => {
      if (!enabled || locked || sceneLayerLocked || !selectedScene || !onScenePatch) return;
      e.preventDefault();
      e.stopPropagation();
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      onDragStart?.();
      setDrag({ kind: "scale", startX: e.clientX, startY: e.clientY, baseScale: sceneScale });
      setHint(snap ? "Skalieren · Raster (5%)" : "Skalieren — diagonal ziehen");
    },
    [enabled, locked, sceneLayerLocked, selectedScene, onScenePatch, sceneScale, snap, onDragStart],
  );

  // ---------------- Overlay drag / scale / rotate ----------------

  const onOverlayMoveDown = useCallback(
    (e: React.PointerEvent, overlay: OverlayLayer) => {
      if (!enabled || locked || !onOverlayPatch) return;
      e.preventDefault();
      e.stopPropagation();
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      onSelectUid?.(overlay.uid);
      onDragStart?.();
      liveDragNodeRef.current = findOverlayDomNode(overlay.uid);
      liveDragPatchRef.current = null;
      setDrag({
        kind: "overlay-move",
        uid: overlay.uid,
        startX: e.clientX,
        startY: e.clientY,
        baseXPct: overlay.xPct,
        baseYPct: overlay.yPct,
      });
      const kindLabel = overlayBadgeForKind(overlay).label;
      setHint(
        overlay.kind === "text"
          ? `${kindLabel} verschieben — Doppelklick zum Editieren`
          : `${kindLabel} verschieben`,
      );
    },
    [enabled, locked, onOverlayPatch, onSelectUid, onDragStart, findOverlayDomNode],
  );

  const onOverlayScaleDown = useCallback(
    (e: React.PointerEvent, overlay: OverlayLayer) => {
      if (!enabled || locked || !onOverlayPatch) return;
      e.preventDefault();
      e.stopPropagation();
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      onSelectUid?.(overlay.uid);
      onDragStart?.();
      liveDragNodeRef.current = findOverlayDomNode(overlay.uid);
      liveDragPatchRef.current = null;
      // baseFontSize is unused for non-text overlays — we keep the field on
      // the drag-state for backwards compatibility but key off `uid` to look
      // up the live overlay inside `doDragPatch` so we always know the kind.
      setDrag({
        kind: "overlay-scale",
        uid: overlay.uid,
        startX: e.clientX,
        startY: e.clientY,
        baseScale: overlay.scale,
        baseFontSize: overlay.kind === "text" ? overlay.fontSize : 0,
      });
      setHint(`${overlayBadgeForKind(overlay).label} skalieren`);
    },
    [enabled, locked, onOverlayPatch, onSelectUid, onDragStart, findOverlayDomNode],
  );

  const onOverlayRotateDown = useCallback(
    (e: React.PointerEvent, overlay: OverlayLayer) => {
      if (!enabled || locked || !onOverlayPatch) return;
      e.preventDefault();
      e.stopPropagation();
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      const rect = overlayRef.current?.getBoundingClientRect();
      const cx = (rect?.left ?? 0) + (overlay.xPct / 100) * displayWidth;
      const cy = (rect?.top ?? 0) + (overlay.yPct / 100) * displayHeight;
      const startAngle =
        (Math.atan2(e.clientY - cy, e.clientX - cx) * 180) / Math.PI;
      onSelectUid?.(overlay.uid);
      onDragStart?.();
      liveDragNodeRef.current = findOverlayDomNode(overlay.uid);
      liveDragPatchRef.current = null;
      setDrag({
        kind: "overlay-rotate",
        uid: overlay.uid,
        startAngleDeg: startAngle,
        baseRotate: overlay.rotate,
        centerX: cx,
        centerY: cy,
      });
      setHint("Rotieren — Shift = 15° Schritte");
    },
    [enabled, locked, onOverlayPatch, onSelectUid, onDragStart, displayWidth, displayHeight, findOverlayDomNode],
  );

  const onPointerMoveRaw = useCallback(
    (e: React.PointerEvent) => {
      if (!drag) return;
      // Capture event values immediately — the React event will be pooled
      // before our rAF callback runs.
      const clientX = e.clientX;
      const clientY = e.clientY;
      const shiftKey = e.shiftKey;
      scheduleUpdate(() => doDragPatch(clientX, clientY, shiftKey));
    },
    // doDragPatch is defined inline below — declared via useCallback after.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [drag, scheduleUpdate],
  );

  const doDragPatch = useCallback(
    (clientX: number, clientY: number, shiftKey: boolean) => {
      if (!drag) return;
      const dxDisp = clientX - drag.startX;
      const dyDisp = clientY - drag.startY;
      // Hold Shift while dragging to bypass the grid (free-positioning).
      const useSnap = snap && !shiftKey;

      if (drag.kind === "logo" && watermark && onWatermarkChange) {
        // Freie Positionierung: Mittelpunkt des Logos folgt dem Cursor 1:1.
        // Mutation läuft direkt auf den iframe-DOM-Knoten — KEIN State-Patch
        // während Drag (sonst rebuildet die Composition pro Frame und das
        // iframe-srcDoc reloadet → sichtbares Stottern).
        const dxPct = (dxDisp / Math.max(1, displayWidth)) * 100;
        const dyPct = (dyDisp / Math.max(1, displayHeight)) * 100;
        let nextX = clamp(drag.baseXPct + dxPct, 0, 100);
        let nextY = clamp(drag.baseYPct + dyPct, 0, 100);
        let snapX: number | null = null;
        let snapY: number | null = null;
        if (useSnap) {
          const stepPctX = 100 / gridCols;
          const stepPctY = 100 / gridRows;
          const sx = snapToStep(nextX, stepPctX, stepPctX * 0.35);
          const sy = snapToStep(nextY, stepPctY, stepPctY * 0.35);
          nextX = sx.value;
          nextY = sy.value;
          if (sx.snapped) snapX = (nextX / 100) * displayWidth;
          if (sy.snapped) snapY = (nextY / 100) * displayHeight;
        }
        setSnapHits({ x: snapX, y: snapY });
        for (const node of logoDragNodesRef.current) {
          node.style.left = `${nextX}%`;
          node.style.top = `${nextY}%`;
          node.style.right = "auto";
          node.style.bottom = "auto";
          node.style.transform = "translate(-50%, -50%)";
        }
        pendingWmCommitRef.current = { xPct: round1(nextX), yPct: round1(nextY) };
      } else if (drag.kind === "logo-resize" && watermark && onWatermarkChange) {
        // Diagonal-Drag → Logo-Größe (Höhe in Komp-Pixeln). Direkt am img-tag
        // mutieren, damit das Resize live & flüssig wirkt; Commit auf release.
        const norm = Math.hypot(displayWidth, displayHeight);
        const sign = dxDisp + dyDisp >= 0 ? 1 : -1;
        const factor = 1 + (sign * Math.hypot(dxDisp, dyDisp) * 2.2) / Math.max(1, norm);
        const next = clamp(Math.round(drag.baseSize * factor), 24, 400);
        for (const node of logoDragNodesRef.current) {
          const img = node.querySelector("img") as HTMLImageElement | null;
          if (img) img.style.height = `${next}px`;
        }
        pendingWmCommitRef.current = { size: next };
      } else if (drag.kind === "scene" && selectedScene && onScenePatch) {
        // Convert display delta → composition px.
        const dxComp = dxDisp / rX;
        const dyComp = dyDisp / rY;
        let nextOffX = drag.baseOffX + dxComp;
        let nextOffY = drag.baseOffY + dyComp;
        let snapX: number | null = null;
        let snapY: number | null = null;
        if (useSnap) {
          // Grid step in composition px corresponding to one display cell.
          const stepCompX = stepX / rX;
          const stepCompY = stepY / rY;
          const sx = snapToStep(nextOffX, stepCompX, stepCompX * 0.3);
          const sy = snapToStep(nextOffY, stepCompY, stepCompY * 0.3);
          nextOffX = sx.value;
          nextOffY = sy.value;
          if (sx.snapped) {
            // The scene box is centered + offset → indicator at its CENTER X.
            snapX = displayWidth / 2 + nextOffX * rX;
          }
          if (sy.snapped) {
            snapY = displayHeight / 2 + nextOffY * rY;
          }
        }
        setSnapHits({ x: snapX, y: snapY });
        onScenePatch(selectedScene.uid, {
          offsetX: Math.round(nextOffX),
          offsetY: Math.round(nextOffY),
        });
      } else if (drag.kind === "scale" && selectedScene && onScenePatch) {
        // Diagonal drag → scale. Use the larger axis delta, normalized to display size.
        const norm = Math.hypot(displayWidth, displayHeight);
        const sign = dxDisp + dyDisp >= 0 ? 1 : -1;
        const factor = 1 + (sign * Math.hypot(dxDisp, dyDisp) * 1.4) / Math.max(1, norm);
        let next = clamp(drag.baseScale * factor, 0.4, 1.6);
        if (useSnap) {
          // Snap scale to 0.05 increments.
          const snapped = snapToStep(next, 0.05, 0.025);
          next = snapped.value;
        }
        onScenePatch(selectedScene.uid, { scale: Number(next.toFixed(2)) });
      } else if (drag.kind === "overlay-move" && onOverlayPatch) {
        // Convert display dx/dy → % of display.
        const dxPct = (dxDisp / Math.max(1, displayWidth)) * 100;
        const dyPct = (dyDisp / Math.max(1, displayHeight)) * 100;
        let nextX = clamp(drag.baseXPct + dxPct, -10, 110);
        let nextY = clamp(drag.baseYPct + dyPct, -10, 110);
        let snapX: number | null = null;
        let snapY: number | null = null;
        if (useSnap) {
          const targetsX = [0, 25, 33.333, 50, 66.667, 75, 100];
          const targetsY = [0, 25, 33.333, 50, 66.667, 75, 100];
          for (const tx of targetsX) {
            if (Math.abs(nextX - tx) < 2.5) {
              nextX = tx;
              snapX = (tx / 100) * displayWidth;
              break;
            }
          }
          for (const ty of targetsY) {
            if (Math.abs(nextY - ty) < 2.5) {
              nextY = ty;
              snapY = (ty / 100) * displayHeight;
              break;
            }
          }
        }
        setSnapHits({ x: snapX, y: snapY });
        const patch: Partial<OverlayLayer> = {
          xPct: Math.round(nextX * 10) / 10,
          yPct: Math.round(nextY * 10) / 10,
        };
        const live = (overlays as OverlayLayer[]).find((o) => o.uid === drag.uid);
        if (live && liveDragNodeRef.current) {
          applyLivePatch(liveDragNodeRef.current, live, patch);
        }
        liveDragPatchRef.current = {
          uid: drag.uid,
          patch: { ...(liveDragPatchRef.current?.patch ?? {}), ...patch },
        };
      } else if (drag.kind === "overlay-scale" && onOverlayPatch) {
        const norm = Math.hypot(displayWidth, displayHeight);
        const sign = dxDisp + dyDisp >= 0 ? 1 : -1;
        const factor = 1 + (sign * Math.hypot(dxDisp, dyDisp) * 1.6) / Math.max(1, norm);
        const live = (overlays as OverlayLayer[]).find((o) => o.uid === drag.uid);
        if (live) {
          const patch = { ...patchForScale(live, factor), scale: drag.baseScale } as Partial<OverlayLayer>;
          if (liveDragNodeRef.current) applyLivePatch(liveDragNodeRef.current, live, patch);
          liveDragPatchRef.current = {
            uid: drag.uid,
            patch: { ...(liveDragPatchRef.current?.patch ?? {}), ...patch },
          };
        }
      } else if (drag.kind === "overlay-rotate" && onOverlayPatch) {
        const angle =
          (Math.atan2(clientY - drag.centerY, clientX - drag.centerX) * 180) / Math.PI;
        let nextRot = drag.baseRotate + (angle - drag.startAngleDeg);
        if (shiftKey) nextRot = Math.round(nextRot / 15) * 15;
        if (nextRot > 180) nextRot -= 360;
        if (nextRot < -180) nextRot += 360;
        const patch: Partial<OverlayLayer> = { rotate: Math.round(nextRot * 10) / 10 };
        const live = (overlays as OverlayLayer[]).find((o) => o.uid === drag.uid);
        if (live && liveDragNodeRef.current) {
          applyLivePatch(liveDragNodeRef.current, live, patch);
        }
        liveDragPatchRef.current = {
          uid: drag.uid,
          patch: { ...(liveDragPatchRef.current?.patch ?? {}), ...patch },
        };
      }
    },
    [
      drag,
      watermark,
      onWatermarkChange,
      selectedScene,
      onScenePatch,
      onOverlayPatch,
      overlays,
      displayWidth,
      displayHeight,
      rX,
      rY,
      snap,
      gridCols,
      gridRows,
      stepX,
      stepY,
      applyLivePatch,
    ],
  );

  const onPointerUp = useCallback(
    () => {
      if (!drag) return;
      // Flush any pending rAF patch immediately so the final position is committed
      // before we tear down drag state.
      if (rafIdRef.current != null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
        const fn = pendingPatchRef.current;
        pendingPatchRef.current = null;
        if (fn) fn();
      }
      // Overlay-Drags committen ihren akkumulierten Patch erst hier — während
      // des Drags wurde nur der DOM-Knoten im iframe live mutiert (ohne den
      // Composer-State zu touchen), um Iframe-Reload-Stottern und
      // Playhead-Reset zu vermeiden.
      const liveCommit = liveDragPatchRef.current;
      liveDragPatchRef.current = null;
      liveDragNodeRef.current = null;
      if (
        liveCommit &&
        onOverlayPatch &&
        (drag.kind === "overlay-move" ||
          drag.kind === "overlay-scale" ||
          drag.kind === "overlay-rotate")
      ) {
        onOverlayPatch(liveCommit.uid, liveCommit.patch);
      }
      // Watermark-Drag-Commit (gleicher Grund wie oben: kein iframe-Reload
      // während Drag, einmal final auf Release). Gilt sowohl für Position
      // (kind === "logo") als auch für Größe (kind === "logo-resize").
      const wmCommit = pendingWmCommitRef.current;
      pendingWmCommitRef.current = null;
      logoDragNodesRef.current = [];
      if (
        wmCommit &&
        watermark &&
        onWatermarkChange &&
        (drag.kind === "logo" || drag.kind === "logo-resize")
      ) {
        onWatermarkChange({ ...watermark, ...wmCommit });
      }
      onDragEnd?.();
      // Watermark-Drag bleibt frei (kein Snap-to-Corner) — Eckpresets gibt's
      // weiterhin via die Position-Buttons im WatermarkPanel.
      setDrag(null);
      setSnapHits({ x: null, y: null });
      setTimeout(() => setHint(null), 1200);
    },
    [drag, onDragEnd, onOverlayPatch, onScenePatch, onWatermarkChange, watermark],
  );

  // Reset hint when disabled.
  useEffect(() => {
    if (!enabled) setHint(null);
  }, [enabled]);

  // ---------- Overlay visibility / bounding-box measurement ----------

  /** Overlays that are visible at the current playhead frame.
   *  Hidden layers (eye toggle off in the Layers panel) and group containers
   *  are skipped — they shouldn't be selectable on canvas. The hidden flag
   *  also cascades from a parent group to its children. */
  const visibleOverlays = useMemo(() => {
    const sec = playheadFrame / Math.max(1, fps);
    const all = overlays as OverlayLayer[];
    const byUid = new Map(all.map((o) => [o.uid, o] as const));
    const isEffectivelyHidden = (o: OverlayLayer): boolean => {
      let cur: OverlayLayer | undefined = o;
      const seen = new Set<string>();
      while (cur && !seen.has(cur.uid)) {
        if (cur.hidden) return true;
        seen.add(cur.uid);
        cur = cur.parentUid ? byUid.get(cur.parentUid) : undefined;
      }
      return false;
    };
    return all.filter(
      (o) =>
        o.kind !== "group" &&
        !isEffectivelyHidden(o) &&
        sec >= o.fromSec - 0.001 &&
        sec < o.fromSec + o.durSec - 0.001,
    );
  }, [overlays, playheadFrame, fps]);

  /**
   * Bounding boxes (in DISPLAY pixels) for every visible overlay. We measure
   * the LIVE rendered DOM node inside the Player by `data-overlay-uid` so
   * the box always wraps the actual text — no font-metric guesswork.
   *
   * Recomputes on overlay-data changes, drag, AND on a window resize.
   * The Player itself owns the rendered text so we have to query through the
   * shared player-shell parent.
   */
  const [overlayBoxes, setOverlayBoxes] = useState<
    Record<string, { left: number; top: number; w: number; h: number }>
  >({});

  const measureOverlayBoxes = useCallback(() => {
    const root = overlayRef.current?.parentElement;
    if (!root) return;
    const rootRect = root.getBoundingClientRect();
    // The composition (inkl. Overlays) wird in einem <iframe srcDoc> gerendert,
    // daher müssen wir die Overlay-Nodes innerhalb des iframe-Dokuments suchen.
    // Inner-Koordinaten sind in Composition-Pixeln (vor dem äußeren CSS-Scale).
    const iframe = root.querySelector("iframe") as HTMLIFrameElement | null;
    let innerDoc: Document | null = null;
    try {
      innerDoc = iframe?.contentDocument ?? null;
    } catch {
      innerDoc = null;
    }
    const iframeRect = iframe?.getBoundingClientRect() ?? null;
    const scaleX = iframeRect ? iframeRect.width / Math.max(1, compositionWidth) : 1;
    const scaleY = iframeRect ? iframeRect.height / Math.max(1, compositionHeight) : 1;

    const next: Record<string, { left: number; top: number; w: number; h: number }> = {};
    for (const o of visibleOverlays) {
      // Erst im Parent-Doc suchen (Fallback für künftige React-side overlays),
      // dann im iframe-Doc. Cross-document querySelector geht nicht.
      let node = root.querySelector(`[data-overlay-uid="${o.uid}"]`) as HTMLElement | null;
      let isInner = false;
      if (!node && innerDoc) {
        node = innerDoc.querySelector(`[data-overlay-uid="${o.uid}"]`) as HTMLElement | null;
        isInner = !!node;
      }
      if (!node) continue;
      const r = node.getBoundingClientRect();
      if (isInner && iframeRect) {
        // r ist relativ zur iframe-Viewport — vor dem äußeren CSS-scale.
        next[o.uid] = {
          left: iframeRect.left - rootRect.left + r.left * scaleX,
          top: iframeRect.top - rootRect.top + r.top * scaleY,
          w: r.width * scaleX,
          h: r.height * scaleY,
        };
      } else {
        next[o.uid] = {
          left: r.left - rootRect.left,
          top: r.top - rootRect.top,
          w: r.width,
          h: r.height,
        };
      }
    }
    // Skip the state update when nothing actually changed — otherwise we
    // re-render every animation frame even when the overlay positions are
    // stable, which (a) wastes CPU and (b) caused stale `drag` state to look
    // pinned because the parent kept re-mounting.
    setOverlayBoxes((prev) => {
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (prevKeys.length === nextKeys.length) {
        let same = true;
        for (const k of nextKeys) {
          const a = prev[k];
          const b = next[k];
          if (
            !a ||
            Math.abs(a.left - b.left) > 0.5 ||
            Math.abs(a.top - b.top) > 0.5 ||
            Math.abs(a.w - b.w) > 0.5 ||
            Math.abs(a.h - b.h) > 0.5
          ) {
            same = false;
            break;
          }
        }
        if (same) return prev;
      }
      return next;
    });
  }, [visibleOverlays, compositionWidth, compositionHeight]);

  // Re-measure when overlays change, when display size changes, or when the
  // player commits DOM (we hook a MutationObserver on the player root so
  // animated content (e.g. text typewriter) re-syncs the box).
  useLayoutEffect(() => {
    measureOverlayBoxes();
    const root = overlayRef.current?.parentElement;
    if (!root) return;
    const ro = new ResizeObserver(() => measureOverlayBoxes());
    ro.observe(root);
    // Also tick at ~30fps while a drag/playback might move things — far
    // cheaper than a 60fps loop and good enough visually.
    const id = window.setInterval(() => measureOverlayBoxes(), 33);
    return () => {
      ro.disconnect();
      window.clearInterval(id);
    };
  }, [measureOverlayBoxes, displayWidth, displayHeight, playheadFrame]);

  // ---------- Inline edit ----------
  const [editingUid, setEditingUid] = useState<string | null>(null);
  const editableRef = useRef<HTMLDivElement | null>(null);

  // Auto-focus + select-all when entering edit mode.
  useEffect(() => {
    if (!editingUid) return;
    const el = editableRef.current;
    if (!el) return;
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }, [editingUid]);

  const commitEdit = useCallback(() => {
    if (!editingUid || !onOverlayPatch) {
      setEditingUid(null);
      return;
    }
    const el = editableRef.current;
    if (el) {
      const text = (el.innerText || "").replace(/\u00A0/g, " ");
      onOverlayPatch(editingUid, { text });
    }
    setEditingUid(null);
  }, [editingUid, onOverlayPatch]);

  // Background click on the overlay container = deselect.
  const onContainerPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.target !== e.currentTarget) return; // ignore bubbled clicks
      if (!onSelectUid) return;
      onSelectUid("");
    },
    [onSelectUid],
  );

  /* ---------- Element-library drop target ---------- */

  const [dropActive, setDropActive] = useState(false);
  const dragLeaveTimerRef = useRef<number | null>(null);

  const ELEMENT_DRAG_MIME = "application/x-jusa-element";

  const hasElementDragData = useCallback((e: React.DragEvent): boolean => {
    const types = e.dataTransfer.types;
    for (let i = 0; i < types.length; i += 1) {
      if (types[i] === ELEMENT_DRAG_MIME) return true;
    }
    return false;
  }, []);

  const onElementDragOver = useCallback(
    (e: React.DragEvent) => {
      if (locked || !onDropElement) return;
      if (!hasElementDragData(e)) return;
      e.preventDefault(); // required to allow drop
      e.dataTransfer.dropEffect = "copy";
      if (!dropActive) setDropActive(true);
      // Cancel any pending leave-debounce while we keep moving inside.
      if (dragLeaveTimerRef.current != null) {
        window.clearTimeout(dragLeaveTimerRef.current);
        dragLeaveTimerRef.current = null;
      }
    },
    [locked, onDropElement, hasElementDragData, dropActive],
  );

  const onElementDragLeave = useCallback(() => {
    // Debounce because dragleave fires on every internal element transition.
    if (dragLeaveTimerRef.current != null) {
      window.clearTimeout(dragLeaveTimerRef.current);
    }
    dragLeaveTimerRef.current = window.setTimeout(() => {
      setDropActive(false);
      dragLeaveTimerRef.current = null;
    }, 80);
  }, []);

  const onElementDrop = useCallback(
    (e: React.DragEvent) => {
      if (locked || !onDropElement) return;
      const raw = e.dataTransfer.getData(ELEMENT_DRAG_MIME);
      if (!raw) return;
      e.preventDefault();
      setDropActive(false);
      try {
        const payload = JSON.parse(raw) as { elementId: string };
        if (!payload?.elementId) return;
        const rect = overlayRef.current?.getBoundingClientRect();
        if (!rect) return;
        const xPct = clampNum(((e.clientX - rect.left) / Math.max(1, displayWidth)) * 100, 0, 100);
        const yPct = clampNum(((e.clientY - rect.top) / Math.max(1, displayHeight)) * 100, 0, 100);
        onDropElement(payload.elementId, round1(xPct), round1(yPct));
      } catch {
        /* ignore malformed payload */
      }
    },
    [locked, onDropElement, displayWidth, displayHeight],
  );

  if (!enabled) return null;

  // Logo-Drag-Handle nur, wenn wir wissen, welche Scene wir editieren —
  // ohne Scene-Scope wäre der Handle mehrdeutig (welches Clip-Logo soll
  // verschoben werden?). Erst Scene auf der Timeline wählen, dann ziehen.
  const showLogo = !!watermark?.enabled && !!wmSceneUid;
  const showScene = !!selectedScene && !locked && !sceneLayerLocked;
  const isDragging = !!drag;
  const showGrid = snap && (isDragging || hint !== null);

  return (
    <div
      ref={overlayRef}
      className={`edit-overlay ${isDragging ? "dragging" : ""} ${locked ? "locked" : ""} ${
        dropActive ? "drop-active" : ""
      }`}
      style={{ width: displayWidth, height: displayHeight }}
      onPointerMove={onPointerMoveRaw}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerDown={onContainerPointerDown}
      onDragOver={onElementDragOver}
      onDragEnter={onElementDragOver}
      onDragLeave={onElementDragLeave}
      onDrop={onElementDrop}
    >
      {/* Grid (rule-of-thirds + fine snap-grid). Only visible while dragging
          with snap enabled — keeps the canvas clean when idle. */}
      {showGrid ? (
        <svg
          className="edit-grid"
          width={displayWidth}
          height={displayHeight}
          viewBox={`0 0 ${displayWidth} ${displayHeight}`}
          aria-hidden
        >
          {/* Fine snap-grid */}
          {Array.from({ length: gridCols - 1 }).map((_, i) => {
            const x = stepX * (i + 1);
            return (
              <line
                key={`gx-${i}`}
                x1={x}
                y1={0}
                x2={x}
                y2={displayHeight}
                stroke="rgba(255,255,255,0.08)"
                strokeWidth={1}
              />
            );
          })}
          {Array.from({ length: gridRows - 1 }).map((_, i) => {
            const y = stepY * (i + 1);
            return (
              <line
                key={`gy-${i}`}
                x1={0}
                y1={y}
                x2={displayWidth}
                y2={y}
                stroke="rgba(255,255,255,0.08)"
                strokeWidth={1}
              />
            );
          })}
          {/* Rule-of-thirds (stronger) */}
          {[1, 2].map((i) => (
            <line
              key={`tx-${i}`}
              x1={(displayWidth / 3) * i}
              y1={0}
              x2={(displayWidth / 3) * i}
              y2={displayHeight}
              stroke="rgba(252, 211, 77, 0.32)"
              strokeWidth={1}
              strokeDasharray="4 6"
            />
          ))}
          {[1, 2].map((i) => (
            <line
              key={`ty-${i}`}
              x1={0}
              y1={(displayHeight / 3) * i}
              x2={displayWidth}
              y2={(displayHeight / 3) * i}
              stroke="rgba(252, 211, 77, 0.32)"
              strokeWidth={1}
              strokeDasharray="4 6"
            />
          ))}
          {/* Center cross */}
          <line
            x1={displayWidth / 2}
            y1={0}
            x2={displayWidth / 2}
            y2={displayHeight}
            stroke="rgba(99, 102, 241, 0.28)"
            strokeWidth={1}
          />
          <line
            x1={0}
            y1={displayHeight / 2}
            x2={displayWidth}
            y2={displayHeight / 2}
            stroke="rgba(99, 102, 241, 0.28)"
            strokeWidth={1}
          />
          {/* Active snap lines (highlight) */}
          {snapHits.x !== null ? (
            <line
              x1={snapHits.x}
              y1={0}
              x2={snapHits.x}
              y2={displayHeight}
              stroke="rgba(244, 114, 182, 0.95)"
              strokeWidth={2}
            />
          ) : null}
          {snapHits.y !== null ? (
            <line
              x1={0}
              y1={snapHits.y}
              x2={displayWidth}
              y2={snapHits.y}
              stroke="rgba(244, 114, 182, 0.95)"
              strokeWidth={2}
            />
          ) : null}
        </svg>
      ) : null}

      {/* Lock badge (top-right) when preview is edit-locked */}
      {locked ? (
        <div className="edit-lock-badge" title="Edit gesperrt — drücke L oder Schloss in der Transport-Bar">
          🔒 Edit gesperrt
        </div>
      ) : null}

      {/* Scene content bounding box (only the BORDER captures pointer in idle —
          the inside is hollow so clicks pass through to the player for play/pause) */}
      {showScene ? (
        <div
          className="edit-scene-box"
          style={{
            left: sceneBox.left,
            top: sceneBox.top,
            width: sceneBox.w,
            height: sceneBox.h,
          }}
          onPointerDown={onSceneDown}
          title="Scene-Inhalt verschieben"
        >
          <div className="edit-scene-handle-tag" onPointerDown={onSceneDown}>
            <span className="edit-scene-icon">⊕</span>
            <span>Scene</span>
          </div>
          <div
            className="edit-scale-handle"
            onPointerDown={onScaleDown}
            title="Skalieren"
            aria-label="Skalieren"
          >
            ⤡
          </div>
        </div>
      ) : null}

      {/* ---------- Free-form overlay selection / handles (text/shape/icon/badge/image) ---------- */}
      {!locked
        ? visibleOverlays.map((o) => {
            // Per-layer Photoshop-style lock: skip the selection box entirely
            // — the layer is still rendered in the player but cannot be
            // grabbed on canvas. Unlock from the Layers panel to edit again.
            // Cascade from parent group too.
            const isEffectivelyLocked = (() => {
              const all = overlays as OverlayLayer[];
              const byUid = new Map(all.map((x) => [x.uid, x] as const));
              let cur: OverlayLayer | undefined = o;
              const seen = new Set<string>();
              while (cur && !seen.has(cur.uid)) {
                if (cur.locked) return true;
                seen.add(cur.uid);
                cur = cur.parentUid ? byUid.get(cur.parentUid) : undefined;
              }
              return false;
            })();
            if (isEffectivelyLocked) return null;
            const box = overlayBoxes[o.uid];
            if (!box) return null;
            const isSel = selectedOverlay?.uid === o.uid;
            const isText = o.kind === "text";
            const isEditing = isText && editingUid === o.uid;
            const PAD = 6;
            const left = box.left - PAD;
            const top = box.top - PAD;
            const w = box.w + PAD * 2;
            const h = box.h + PAD * 2;
            const badge = overlayBadgeForKind(o);
            const titleStr = isText
              ? "Klicken auswählen · Doppelklick editieren · Ziehen verschieben"
              : "Klicken auswählen · Ziehen verschieben";
            return (
              <div
                key={o.uid}
                className={`edit-overlay-box overlay-kind-${o.kind} ${
                  isSel ? "selected" : ""
                } ${isEditing ? "editing" : ""}`}
                style={{ left, top, width: w, height: h }}
                onPointerDown={(e) => {
                  if (isEditing) return;
                  onOverlayMoveDown(e, o);
                }}
                onDoubleClick={(e) => {
                  if (!isText) return;
                  e.stopPropagation();
                  e.preventDefault();
                  onSelectUid?.(o.uid);
                  setEditingUid(o.uid);
                }}
                title={titleStr}
              >
                {/* Inline editable text — only available for text overlays. */}
                {isEditing && o.kind === "text" ? (
                  <div
                    ref={editableRef}
                    className="edit-overlay-editable"
                    contentEditable
                    suppressContentEditableWarning
                    spellCheck={false}
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        e.preventDefault();
                        setEditingUid(null);
                      }
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        commitEdit();
                      }
                    }}
                    onBlur={() => commitEdit()}
                    style={{
                      fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                      fontWeight: o.weight,
                      fontSize: (o.fontSize * displayWidth) / Math.max(1, compositionWidth) * o.scale,
                      textAlign: o.align,
                      lineHeight: 1.05,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent:
                        o.align === "left"
                          ? "flex-start"
                          : o.align === "right"
                          ? "flex-end"
                          : "center",
                      padding: PAD,
                      boxSizing: "border-box",
                      outline: "none",
                      color: "#fff",
                      background: "rgba(15,23,42,0.92)",
                      border: "2px solid #38bdf8",
                      borderRadius: 8,
                    }}
                  >
                    {o.text || ""}
                  </div>
                ) : null}

                {isSel && !isEditing ? (
                  <>
                    <div
                      className="edit-overlay-tag"
                      onPointerDown={(e) => onOverlayMoveDown(e, o)}
                    >
                      <span className="edit-overlay-tag-icon">{badge.glyph}</span>
                      <span>{badge.label}</span>
                    </div>
                    <div
                      className="edit-overlay-scale"
                      onPointerDown={(e) => onOverlayScaleDown(e, o)}
                      title="Größe ziehen"
                      aria-label="Größe"
                    >
                      ⤡
                    </div>
                    <div
                      className="edit-overlay-rotate"
                      onPointerDown={(e) => onOverlayRotateDown(e, o)}
                      title="Rotieren (Shift = 15°)"
                      aria-label="Rotieren"
                    >
                      ↻
                    </div>
                  </>
                ) : null}
              </div>
            );
          })
        : null}

      {/* Note: der frühere "+ T Text"-Button oben links im Player wurde
          entfernt. Funktion ist redundant zur QuickAdd-Leiste über dem
          Player und nimmt nur Platz weg. Tastenkürzel "T" funktioniert
          weiterhin (siehe QuickAdd / Composer Keyboard-Handler). */}

      {/* Logo (watermark) drag handle — minimal, ohne Pill/Label, damit man
          das Logo selbst sieht. Klick + Drag = verschieben. Eck-Handle unten
          rechts = live skalieren. Beide Aktionen mutieren den iframe-DOM
          während der Bewegung (kein State-Reload → kein Stutter). */}
      {showLogo && !locked && logoBox ? (
        <div
          className="edit-logo-frame"
          style={{
            left: logoBox.left - 4,
            top: logoBox.top - 4,
            width: logoBox.w + 8,
            height: logoBox.h + 8,
          }}
          onPointerDown={onLogoDown}
          title="Logo frei verschieben — Eck-Griff zum Skalieren"
        >
          <div
            className="edit-logo-resize"
            onPointerDown={onLogoResizeDown}
            title="Logo skalieren"
            aria-label="Logo skalieren"
          />
        </div>
      ) : null}

      {/* Brief hint when something just happened (snap, drag start) */}
      {hint ? <div className="edit-hint-banner">{hint}</div> : null}
    </div>
  );
};

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
function round1(v: number): number {
  return Math.round(v * 10) / 10;
}
